import { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../../utils/axios';
import { getLeaderBoard, isFirstQualifiedApp } from '../../utils/urls';
import FirstQualifiedAppCelebration from './FirstQualifiedAppCelebration';
import breadcrumbLogo from '../../Images/Breadcrumb_logo.png';
import { Calendar } from 'primereact/calendar';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './newLeaderBoard.css';
import { useNavigate } from 'react-router-dom';
import useWebSocket from 'react-use-websocket';
import appNotificationSound from '../../media/universfield-new-notification-050-494248.mp3';
import docNotificationSound from '../../media/sound-for-docs-in.mp3';

/* ── Icons ── */
const ICONS = {
    hustle: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
            <path d="M12 2c0 4-4 7-4 11a4 4 0 0 0 8 0c0-4-3-7-3-11Z" />
            <path d="M10 15c0 1.1.9 2 2 2" />
        </svg>
    ),
    apps: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
            <path d="M7 4h7l4 4v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
            <path d="M14 4v4h4" /><path d="M8.5 13h6M8.5 16.5h4" />
        </svg>
    ),
    calls: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
            <path d="M4.5 5.5c0 8 6 14 14 14l2.5-2.5-4-3-2.5 1.5c-2-1-3.5-2.5-4.5-4.5L11.5 8l-3-4L6 4.5Z" />
        </svg>
    ),
    total_docs_in_apps: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z" />
            <path d="M14 2v6h6M8 13h8M8 17h5" />
        </svg>
    ),
};

/* ── Metrics config ── */
const METRICS = [
    { key: 'hustle', label: 'Most Hustle', col: 'Calls', by: 'most hustle', unit: '', better: 'high' },
    { key: 'apps', label: 'Most Apps', col: 'Apps', by: 'most apps', unit: '', better: 'high' },
    { key: 'calls', label: 'Most Calls', col: 'Calls', by: 'most calls', unit: '', better: 'high' },
    { key: 'total_docs_in_apps', label: 'Most Docs', col: 'Docs', by: 'most docs in apps', unit: '', better: 'high' },
];

/* API trailing-delta field lookup */
const DELTA_KEY = { apps: 'trailing_apps', calls: 'trailing_calls' };

/* ── Prize medal SVG ── */
function PrizeSVG({ place }) {
    const P = {
        1: { face: '#f6b823', ring: '#fff0c2', edge: '#a86e0e', ribbon: '#d94a3d', txt: '#7a4d00' },
        2: { face: '#c4ccd4', ring: '#ffffff', edge: '#828b94', ribbon: '#5b6570', txt: '#4a525b' },
        3: { face: '#d98a4e', ring: '#f4cfae', edge: '#9c5a28', ribbon: '#4a6a8a', txt: '#5c3210' },
    }[place];
    return (
        <svg viewBox="0 0 40 52" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
            <path d="M13 26 L8 50 L15 45 L20 51 L25 45 L32 50 L27 26 Z" fill={P.ribbon} />
            <circle cx="20" cy="19" r="16" fill={P.edge} />
            <circle cx="20" cy="19" r="14" fill={P.face} />
            <circle cx="20" cy="19" r="14" fill="none" stroke={P.ring} strokeWidth="2" />
            <text x="20" y="26" textAnchor="middle" fontFamily="Barlow Condensed, sans-serif"
                fontWeight="800" fontSize="18" fill={P.txt}>{place}</text>
        </svg>
    );
}

/* ── Delta indicator ── */
function Delta({ value, better }) {
    if (value === 0 || value == null) return <span className="d flat">±0</span>;
    const good = better === 'low' ? value < 0 : value > 0;
    const sign = value > 0 ? '+' : '−';
    return <span className={`d ${good ? 'up' : 'down'}`}>{sign}{Math.abs(value)}</span>;
}

/* ── Individual stat cell ── */
function StatCell({ person, metricKey, primary, cellClass }) {
    const metric = METRICS.find(m => m.key === metricKey) || { col: metricKey, unit: '', better: 'high' };
    const value = person[metricKey] ?? 0;
    const deltaKey = DELTA_KEY[metricKey];
    const delta = deltaKey != null ? (person[deltaKey] ?? null) : null;
    return (
        <div className={`${cellClass}${primary ? ' primary' : ''}${value === 0 ? ' zero' : ''}`}>
            <span className="lab">{metric.col}</span>
            <div className="v">{value}{metric.unit}</div>
            {delta !== null && <Delta value={delta} better={metric.better} />}
        </div>
    );
}

/* ── TV stat set — fixed column order: calls | apps | docs ── */
function TvStatSet({ person, active, cellClass }) {
    return (
        <>
            <StatCell person={person} metricKey="calls" primary={active === 'calls'} cellClass={cellClass} />
            <StatCell person={person} metricKey="apps" primary={active === 'apps'} cellClass={cellClass} />
            <StatCell person={person} metricKey="total_docs_in_apps" primary={active === 'total_docs_in_apps'} cellClass={cellClass} />
        </>
    );
}

/* ── Sort dropdown menu ── */
function SortMenu({ active, onSelect }) {
    return (
        <div className="sort-menu">
            {METRICS.filter(m => !m.noSort).map(m => (
                <button
                    key={m.key}
                    className={m.key === active ? 'on' : ''}
                    onClick={e => { e.stopPropagation(); onSelect(m.key); }}
                >
                    {ICONS[m.key]}<span>{m.label}</span>
                </button>
            ))}
        </div>
    );
}

/* ── Avatar with initials fallback ── */
const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

function Avatar({ src, name, className }) {
    const [failed, setFailed] = useState(false);
    if (!src || failed) {
        return (
            <div className={`nlb-initials${className ? ` ${className}` : ''}`}>
                {getInitials(name)}
            </div>
        );
    }
    return (
        <img
            className={className}
            src={src}
            alt={name}
            onError={() => setFailed(true)}
        />
    );
}

/* ── Date helpers ── */
const toISO = (date) => date.toLocaleDateString('en-CA');

/* ── Main component ── */
export default function NewLeaderBoard() {
    const [today, setToday] = useState(() => new Date());

    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState('hustle');
    const [dates, setDates] = useState(() => { const t = new Date(); return [t, t]; });
    const [menuOpen, setMenuOpen] = useState(null); // 'mob' | 'tv' | null
    const [calOpen, setCalOpen] = useState(false);
    const [mobMenuPos, setMobMenuPos] = useState(null);
    const mobSortRef = useRef(null);
    const navigate = useNavigate();
    const datesRef = useRef(dates);
    datesRef.current = dates;
    const todayIsoRef = useRef(toISO(today));
    const isFirstConnectRef = useRef(true);
    const debounceTimerRef = useRef(null);
    const midnightTimerRef = useRef(null);
    const abortRef = useRef(null);
    const loadingControllerRef = useRef(null); // tracks which controller is responsible for clearing the spinner
    const sortedPeopleRef = useRef([]);

    /* ── First-qualified-app celebration ── */
    const [celebrationName, setCelebrationName] = useState(null);

    /* ── App-recipient flash (1 min) ── */
    const [flashingUserId, setFlashingUserId] = useState(null);
    const flashTimerRef = useRef(null);

    /* ── Sound ── */
    const [soundEnabled, setSoundEnabled] = useState(true);
    const soundEnabledRef = useRef(true);
    soundEnabledRef.current = soundEnabled;
    const appAudioRef = useRef(null);
    const docAudioRef = useRef(null);
    const prevPeopleRef = useRef([]);

    const playAppSound = useCallback(() => {
        if (!soundEnabledRef.current) return;
        try {
            if (!appAudioRef.current) {
                appAudioRef.current = new Audio(appNotificationSound);
            }
            appAudioRef.current.currentTime = 0;
            appAudioRef.current.play().catch(() => {});
        } catch (_) {}
    }, []);

    const playDocSound = useCallback(() => {
        if (!soundEnabledRef.current) return;
        try {
            if (!docAudioRef.current) {
                docAudioRef.current = new Audio(docNotificationSound);
            }
            docAudioRef.current.currentTime = 0;
            docAudioRef.current.play().catch(() => {});
        } catch (_) {}
    }, []);

    /* ── Day rollover — store today's date on load, reload the page when it changes ── */
    useEffect(() => {
        localStorage.setItem('lb_date', toISO(new Date()));
        midnightTimerRef.current = setInterval(() => {
            if (localStorage.getItem('lb_date') !== toISO(new Date())) {
                window.location.reload();
            }
        }, 60_000);
        return () => clearInterval(midnightTimerRef.current);
    }, []); // eslint-disable-line

    /* Close menus/calendar on outside click */
    useEffect(() => {
        if (!menuOpen && !calOpen) return;
        const handler = () => { setMenuOpen(null); setCalOpen(false); };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [menuOpen, calOpen]);

    /* ── Dev shortcuts: Shift+A = app sound, Shift+D = doc sound, Shift+F = celebration demo ── */
    useEffect(() => {
        const handler = (e) => {
            if (e.shiftKey && e.key === 'F') {
                e.preventDefault();
                setCelebrationName('John Smith');
                return;
            }
            if (!e.shiftKey) return;
            if (e.key === 'A') playAppSound();
            if (e.key === 'D') playDocSound();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [playAppSound, playDocSound]);

    const handleCelebrationDone = useCallback(() => {
        setCelebrationName(null);
        const first = sortedPeopleRef.current[0];
        if (first?.custom_user_id != null) {
            setFlashingUserId(first.custom_user_id);
            clearTimeout(flashTimerRef.current);
            flashTimerRef.current = setTimeout(() => setFlashingUserId(null), 60_000);
        }
    }, []);

    const fetchLeaderboard = useCallback((showLoading = true) => {
        const [start, end] = datesRef.current;
        if (!start || !end) return;

        // Cancel any in-flight request before starting a new one
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        if (showLoading) {
            loadingControllerRef.current = controller;
            setLoading(true);
        }
        axios
            .get(`${getLeaderBoard}?type=callers&start_date=${toISO(start)}&end_date=${toISO(end)}`, {
                signal: controller.signal,
            })
            .then(res => {
                if (res.status === 200) {
                    const newPeople = res.data;
                    const prev = prevPeopleRef.current;
                    if (prev.length > 0) {
                        let newDoc = false, newApp = false;
                        newPeople.forEach(p => {
                            const old = prev.find(o => o.user_id === p.user_id);
                            if (!old) {
                                if ((p.total_docs_in_apps ?? 0) > 0) newDoc = true;
                                else if ((p.apps ?? 0) > 0) newApp = true;
                            } else {
                                if ((p.total_docs_in_apps ?? 0) > (old.total_docs_in_apps ?? 0)) newDoc = true;
                                else if ((p.apps ?? 0) > (old.apps ?? 0)) newApp = true;
                            }
                        });
                        if (newDoc) playDocSound();
                        else if (newApp) playAppSound();
                    }
                    prevPeopleRef.current = newPeople;
                    setPeople(newPeople);
                }
            })
            .catch(err => {
                // Ignore cancellation — a newer request is already in flight
                if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
                setPeople([]);
            })
            .finally(() => {
                // Clear the spinner only if this controller is still responsible for it.
                // A showLoading=false request that supersedes a showLoading=true one leaves
                // loadingControllerRef pointing at the old controller, so it clears correctly.
                if (loadingControllerRef.current === controller) setLoading(false);
            });
    }, [playAppSound, playDocSound]);

    /* Fetch data when date range changes */
    useEffect(() => {
        fetchLeaderboard(true);
    }, [dates]);

    const handleWsMessage = useCallback((message) => {
        try {
            const parsed = JSON.parse(message.data);
            console.log('[Leaderboard WS] Message:', parsed);

            if (parsed.app_id && parsed.custom_user_id) {
                const { app_id, custom_user_id } = parsed;

                // Flash the row of whoever received this app for 1 minute
                setFlashingUserId(custom_user_id);
                clearTimeout(flashTimerRef.current);
                flashTimerRef.current = setTimeout(() => setFlashingUserId(null), 60_000);

                if (app_id && custom_user_id) {
                    axios.get(isFirstQualifiedApp(custom_user_id, app_id), {
                        headers: { 'Content-Type': 'application/json' }
                    }).then((res) => {
                        if (res.status === 200 && res.data.is_first_qualified_app) {
                            setCelebrationName([res.data.first_name, res.data.last_name].filter(Boolean).join(' ') || 'Our New Member');
                        }
                    }).catch(console.error);
                }
                return;
            }

            // Check if the local date has rolled over since last we knew
            const nowIso = toISO(new Date());
            if (nowIso !== todayIsoRef.current) {
                const newToday = new Date();
                const [start, end] = datesRef.current;
                if (end && toISO(end) === todayIsoRef.current) {
                    setDates([start, newToday]);
                    // dates change triggers fetchLeaderboard via the dates useEffect
                }
                todayIsoRef.current = nowIso;
                setToday(newToday);
                return;
            }

            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => fetchLeaderboard(false), 500);
        } catch (err) {
            console.error('WebSocket parse error:', err);
        }
    }, [fetchLeaderboard]);

    const { getWebSocket } = useWebSocket('wss://backendapi.bcrumbs.com/ws/leaderboard/', {
        onMessage: handleWsMessage,
        onOpen: () => {
            if (isFirstConnectRef.current) {
                isFirstConnectRef.current = false;
                console.log('[Leaderboard WS] Connected');
            } else {
                console.log('[Leaderboard WS] Reconnected');
            }
        },
        onClose: () => console.log('[Leaderboard WS] Disconnected'),
        onError: (err) => console.error('[Leaderboard WS] Error', err),
        shouldReconnect: () => true,
        reconnectAttempts: 10000,
        reconnectInterval: 3000,
    });

    /* On tab focus: check date rollover, refetch silently, reconnect WS if dropped */
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            const nowIso = toISO(new Date());
            if (nowIso !== todayIsoRef.current) {
                const newToday = new Date();
                const [start, end] = datesRef.current;
                if (end && toISO(end) === todayIsoRef.current) {
                    setDates([start, newToday]); // triggers fetch via the dates useEffect
                }
                todayIsoRef.current = nowIso;
                setToday(newToday);
            } else {
                fetchLeaderboard(false);
            }
            const ws = getWebSocket();
            if (ws && ws.readyState !== WebSocket.OPEN) {
                ws.close();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [fetchLeaderboard, getWebSocket]);

    /* Derived state */
    const activeMetric = METRICS.find(m => m.key === active);

    const sortedPeople = (() => {
        const filtered = [...people].filter(p => (p.apps ?? 0) > 0 || (p.calls ?? 0) > 0 || (p.total_docs_in_apps ?? 0) > 0);

        if (active === 'hustle') {
            // Most Hustle: dial leader is #1 by default.
            // A person can rank above them only if they have >= 80% of the dial leader's dials AND more apps.
            // Ties on apps broken by Docs In (still requiring >= 80% threshold), then by dials.
            // Those below the 80% threshold rank among themselves by dials only.
            const maxDials = Math.max(...filtered.map(p => p.calls ?? 0), 0);
            const threshold = maxDials * 0.8;
            return filtered.sort((a, b) => {
                const aQual = (a.calls ?? 0) >= threshold;
                const bQual = (b.calls ?? 0) >= threshold;
                if (aQual && bQual) {
                    const appDiff = (b.apps ?? 0) - (a.apps ?? 0);
                    if (appDiff !== 0) return appDiff;
                    const docDiff = (b.total_docs_in_apps ?? 0) - (a.total_docs_in_apps ?? 0);
                    if (docDiff !== 0) return docDiff;
                    return (b.calls ?? 0) - (a.calls ?? 0);
                }
                if (aQual) return -1;
                if (bQual) return 1;
                return (b.calls ?? 0) - (a.calls ?? 0);
            });
        }

        const chain = ['total_docs_in_apps', 'apps', 'calls'];
        const sortKeys = [active, ...chain.filter(k => k !== active)];
        return filtered.sort((a, b) => {
            for (const key of sortKeys) {
                const diff = (b[key] ?? 0) - (a[key] ?? 0);
                if (diff !== 0) return diff;
            }
            return 0;
        });
    })();

    sortedPeopleRef.current = sortedPeople;
    const champ = sortedPeople[0];
    // For hustle mode, primary display is calls; secondary is apps
    const displayMetricKey = active === 'hustle' ? 'calls' : active;
    const sec = active === 'hustle' ? 'apps' : (active === 'apps' ? 'calls' : 'apps'); // secondary metric (mobile)

    const handleSetActive = (key) => { setActive(key); setMenuOpen(null); };

    const openMobMenu = (e) => {
        e.stopPropagation();
        if (menuOpen !== 'mob' && mobSortRef.current) {
            const r = mobSortRef.current.getBoundingClientRect();
            setMobMenuPos({ top: r.bottom + 6, left: r.left + r.width / 2 });
        }
        setMenuOpen(prev => prev === 'mob' ? null : 'mob');
        setCalOpen(false);
    };

    /* ── Render ── */
    return (
        <>
        {celebrationName && (
            <FirstQualifiedAppCelebration
                applicantName={celebrationName}
                onDone={handleCelebrationDone}
            />
        )}
        <div className={`nlb${sortedPeople.length > 0 && sortedPeople.length <= 3 ? ' nlb-few' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="bgfx"></div>
            {/* ── Topbar ── */}
            <div className="topbar">
                {/* <div onClick={()=> navigate("/")} className="wordmark">breadcrumbs.</div> */}
                <img  src={breadcrumbLogo} onClick={() => navigate("/")} style={{ width: '130px', height: '28px', cursor: "pointer" }} alt="" />

                {/* Right-side controls: sound toggle + date pill */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {/* Sound toggle */}
                <button
                    onClick={() => setSoundEnabled(v => !v)}
                    title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: soundEnabled ? '#5bff3b' : 'rgba(255,255,255,0.3)', padding: '4px 6px', borderRadius: '8px', display: 'flex', alignItems: 'center' }}
                >
                    {soundEnabled ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="20" height="20">
                            <path d="M11 5 6 9H2v6h4l5 4V5Z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" width="20" height="20">
                            <path d="M11 5 6 9H2v6h4l5 4V5Z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
                        </svg>
                    )}
                </button>

                {/* Date pill + inline PrimeReact calendar dropdown */}
                <div className="nlb-datewrap" onClick={e => e.stopPropagation()}>
                    <button
                        className={`datepill${calOpen ? ' open' : ''}`}
                        onClick={() => { setCalOpen(v => !v); setMenuOpen(null); }}
                    >
                        <svg className="dpi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                            <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
                            <path d="M3 9h18M8 2.5v4M16 2.5v4" />
                        </svg>
                        <span>
                            {dates[0]?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}
                            {' – '}
                            {(dates[1] || dates[0])?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}
                        </span>
                        <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="m6 9 6 6 6-6" />
                        </svg>
                    </button>
                    {calOpen && (
                        <div className="nlb-cal-drop" onClick={e => e.stopPropagation()}>
                            <Calendar
                                value={dates}
                                onChange={e => {
                                    const val = e.value;
                                    if (!Array.isArray(val)) return;
                                    setDates(val);
                                    if (val[0] && val[1]) setCalOpen(false);
                                }}
                                selectionMode="range"
                                inline
                                maxDate={today}
                            />
                        </div>
                    )}
                </div>
                </div>
            </div>

            {/* ── Champion banner (TV only via CSS) ── */}
            <div className="champ">
                <img
                    className="logomark"
                    src="https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a4e5ebd46e5b517c506cb09.webp"
                    alt="Breadwinners"
                    onError={e => { e.target.style.display = 'none'; }}
                />
                <div className="banner">
                    <div className="shineclip"><span className="shine" /></div>
                    {loading ? (
                        <div className="cwrap">
                            <div className="skel-block" style={{ width: '13vh', height: '13vh', borderRadius: '50%', flex: 'none' }} />
                            <div className="cinfo">
                                <div className="skel-block" style={{ width: '14vh', height: '1.8vh', borderRadius: '0.5vh', marginBottom: '0.9vh' }} />
                                <div className="skel-block" style={{ width: '28vh', height: '4.8vh', borderRadius: '0.8vh', marginBottom: '0.7vh' }} />
                                <div className="skel-block" style={{ width: '18vh', height: '1.9vh', borderRadius: '0.5vh' }} />
                            </div>
                        </div>
                    ) : champ ? (
                        <div className="cwrap">
                            <Avatar src={champ.image} name={champ.name} className="cav" />
                            <div className="cinfo">
                                <div className="ctag">🏆 Today's Breadwinner</div>
                                <div className="cname">{champ.name}</div>
                                <div className="cstat">
                                    <b>{champ[displayMetricKey]}{activeMetric?.unit}</b> · {activeMetric?.by}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* TV sort control */}
                    <div
                        className="sortrow tv-sort"
                        style={{ padding: 0, display: 'block' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div
                            className={`sortbtn${menuOpen === 'tv' ? ' open' : ''}`}
                            onClick={e => { e.stopPropagation(); setMenuOpen(menuOpen === 'tv' ? null : 'tv'); setCalOpen(false); }}
                        >
                            <span className="lbl">Sorted by</span>
                            <span className="si">{ICONS[active]}</span>
                            <span className="cur">{activeMetric?.label}</span>
                            <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="m6 9 6 6 6-6" />
                            </svg>
                        </div>
                        {menuOpen === 'tv' && <SortMenu active={active} onSelect={handleSetActive} />}
                    </div>
                </div>
            </div>

            {/* ── Hero (mobile only via CSS) ── */}
            <div className="hero">
                <img className="logo" src="https://assets.cdn.filesafe.space/lmdPZBnct1qETepFwbdi/media/6a4e5ebd46e5b517c506cb09.webp" alt="Breadwinners" />
                <div className="lead">Leaderboard</div>
            </div>

            {/* ── Sort row (mobile) ── */}
            <div className="sortrow mob-sort" onClick={e => e.stopPropagation()}>
                <div
                    ref={mobSortRef}
                    className={`sortbtn${menuOpen === 'mob' ? ' open' : ''}`}
                    onClick={openMobMenu}
                >
                    <span className="lbl">Sorted by</span>
                    <span className="si">{ICONS[active]}</span>
                    <span className="cur">{activeMetric?.label}</span>
                    <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m6 9 6 6 6-6" />
                    </svg>
                </div>
            </div>

            {/* Mobile sort menu — fixed to viewport so it isn't clipped by scroll containers */}
            {menuOpen === 'mob' && mobMenuPos && (
                <div
                    className="sort-menu"
                    style={{ position: 'fixed', top: mobMenuPos.top, left: mobMenuPos.left, transform: 'translateX(-50%)', zIndex: 99999 }}
                    onClick={e => e.stopPropagation()}
                >
                    {METRICS.filter(m => !m.noSort).map(m => (
                        <button
                            key={m.key}
                            className={m.key === active ? 'on' : ''}
                            onClick={e => { e.stopPropagation(); handleSetActive(m.key); }}
                        >
                            {ICONS[m.key]}<span>{m.label}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* ── Column headers (mobile) ── */}
            <div className="colhead">
                <div className="h-spacer" />
                <div className="h-col">{METRICS.find(m => m.key === sec)?.col}</div>
                <div className="h-col pri">{activeMetric?.col}</div>
            </div>

            {/* ── Mobile board ── */}
            <div className="board">
                {loading ? (
                    Array.from({ length: 7 }, (_, i) => (
                        <div key={i} className="row skel-row">
                            <div className="rank">
                                <div className="skel-block" style={{ width: 14, height: 20, borderRadius: 4 }} />
                            </div>
                            <div className="avatar">
                                <div className="skel-block" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </div>
                            <div className="who">
                                <div className="skel-block" style={{ width: '68%', height: 14, borderRadius: 6, marginBottom: 6 }} />
                                <div className="skel-block" style={{ width: '42%', height: 9, borderRadius: 4 }} />
                            </div>
                            <div className="stats">
                                <div className="stat">
                                    <div className="skel-block" style={{ width: 26, height: 22, borderRadius: 5 }} />
                                </div>
                                <div className="stat primary">
                                    <div className="skel-block" style={{ width: 26, height: 28, borderRadius: 5 }} />
                                </div>
                            </div>
                        </div>
                    ))
                ) : sortedPeople.length === 0 ? (
                    <div className="empty-msg">No data for this period</div>
                ) : (
                    sortedPeople.map((p, i) => {
                        const place = i + 1;
                        return (
                            <div
                                key={p.user_id || i}
                                className={`row${place <= 3 ? ` r${place}` : ''}${p.custom_user_id === flashingUserId ? ' nlb-flashing' : ''}`}
                                style={{ animationDelay: `${Math.min(i * 45, 270)}ms` }}
                            >
                                <div className="rank">{place}</div>
                                <div className="avatar">
                                    <Avatar src={p.image} name={p.name} />
                                    {place <= 3 && (
                                        <div className="prize"><PrizeSVG place={place} /></div>
                                    )}
                                </div>
                                <div className="who">
                                    <div className="nm">{p.name}</div>
                                    <div className="co">{p.company}</div>
                                </div>
                                <div className="stats">
                                    <StatCell person={p} metricKey={sec} primary={false} cellClass="stat" />
                                    <StatCell person={p} metricKey={displayMetricKey} primary={true} cellClass="stat" />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* ── TV Podium — top 3 (left column) ── */}
            <div className="podium">
                {loading ? (
                    [1, 2, 3].map(place => (
                        <div key={place} className={`prow r${place} skel-row`}>
                            <div className="prank">
                                <div className="skel-block" style={{ width: '4.5vh', height: '5vh', borderRadius: '0.8vh' }} />
                            </div>
                            <div className="pav">
                                <div className="skel-block" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </div>
                            <div className="pwho">
                                <div className="skel-block" style={{ width: '62%', height: '3.2vh', borderRadius: '0.7vh', marginBottom: '0.8vh' }} />
                                <div className="skel-block" style={{ width: '38%', height: '1.6vh', borderRadius: '0.5vh' }} />
                            </div>
                            <div className="pstats">
                                {[0, 1, 2].map(j => (
                                    <div key={j} className={`pstat${j === 1 ? ' primary' : ''}`}>
                                        <div className="skel-block" style={{ width: '55%', height: '1.4vh', borderRadius: '0.4vh', marginBottom: '0.8vh' }} />
                                        <div className="skel-block" style={{ width: '48%', height: j === 1 ? '8vh' : '6vh', borderRadius: '0.7vh' }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    sortedPeople.slice(0, 3).map((p, i) => {
                        const place = i + 1;
                        return (
                            <div key={p.user_id || i} className={`prow r${place}${p.custom_user_id === flashingUserId ? ' nlb-flashing' : ''}`}>
                                <div className="prank">{place}</div>
                                <div className="pav">
                                    <Avatar src={p.image} name={p.name} />
                                    <div className="pprize"><PrizeSVG place={place} /></div>
                                </div>
                                <div className="pwho">
                                    <div className="pname">{p.name}</div>
                                    <div className="pco">{p.company}</div>
                                </div>
                                <div className="pstats">
                                    <TvStatSet person={p} active={active} cellClass="pstat" />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* ── TV Rest — positions 4+ (right column) ── */}
            <div className="rest">
                {loading ? (
                    Array.from({ length: 7 }, (_, i) => (
                        <div key={i} className="rrow skel-row">
                            <div className="rrank">
                                <div className="skel-block" style={{ width: '3vh', height: '2.5vh', borderRadius: '0.5vh' }} />
                            </div>
                            <div className="rav">
                                <div className="skel-block" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </div>
                            <div className="rwho">
                                <div className="skel-block" style={{ width: '60%', height: '2vh', borderRadius: '0.5vh', marginBottom: '0.5vh' }} />
                                <div className="skel-block" style={{ width: '36%', height: '1.2vh', borderRadius: '0.4vh' }} />
                            </div>
                            <div className="rstats">
                                {[0, 1, 2].map(j => (
                                    <div key={j} className={`rstat${j === 1 ? ' primary' : ''}`}>
                                        <div className="skel-block" style={{ width: '50%', height: '1.1vh', borderRadius: '0.3vh', marginBottom: '0.5vh' }} />
                                        <div className="skel-block" style={{ width: '44%', height: j === 1 ? '5vh' : '3.5vh', borderRadius: '0.6vh' }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    sortedPeople.slice(3).map((p, i) => (
                        <div key={p.user_id || i} className={`rrow${p.custom_user_id === flashingUserId ? ' nlb-flashing' : ''}`}>
                            <div className="rrank">{i + 4}</div>
                            <div className="rav">
                                <Avatar src={p.image} name={p.name} />
                            </div>
                            <div className="rwho">
                                <div className="rname">{p.name}</div>
                                <div className="rco">{p.company}</div>
                            </div>
                            <div className="rstats">
                                <TvStatSet person={p} active={active} cellClass="rstat" />
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* ── Footer ── */}
            <div className="footer">
                <div className="live">
                    <span className="dot" /> Updates in real time
                </div>
            </div>
        </div>
        </>
    );
}
